import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuthStore } from '@/stores/authStore';
import { getPlatformBranding, platformLogoSrc } from '@/lib/platformBranding';
import BuildStamp from '@/components/BuildStamp';
import ApkStamp from '@/components/ApkStamp';
import { NewBadge } from '@/components/NewBadge';

export default function LoginPage({ setIsAuthenticated }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const authLogin = useAuthStore(s => s.login);
  // SmartoERP PLATFORM identity (name + logo) from the central source — NOT
  // tenant branding. Visual theme colors keep their platform defaults.
  const [brand, setBrand] = useState({ app_name: 'SmartoERP', tagline: 'Business Management Platform', login_bg_color: '#ea580c', primary_color: '#f97316', logo_url: '' });

  useEffect(() => {
    getPlatformBranding().then(b => {
      if (b) setBrand(prev => ({ ...prev, app_name: b.app_name || prev.app_name, logo_url: b.logo_url || '' }));
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await authLogin(email, password);
      
      // Check if password change is required
      if (data.must_change_password) {
        toast.info('Please change your password');
        setIsAuthenticated(true);
        navigate('/change-password?forced=true');
        return;
      }
      
      toast.success('Login successful!');
      setIsAuthenticated(true);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand.login_bg_color || brand.primary_color || '#E8501A'} 0%, ${brand.primary_color || '#F5841F'} 50%, ${brand.accent_color || '#F5A623'} 100%)` }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        {/* Decorative circles */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-48 -right-24 w-[500px] h-[500px] rounded-full bg-white/5" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-white/3" />
        
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          {/* Brand Icon + Text */}
          <div className="flex items-center gap-3 mb-8 cursor-pointer" onClick={() => navigate('/landing')} data-testid="login-logo-link">
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand.login_bg_color || '#E8501A'} 0%, ${brand.primary_color || '#F5841F'} 50%, ${brand.accent_color || '#F5A623'} 100%)` }}>
              {brand.logo_url ? (
                <img src={platformLogoSrc(brand)} alt={brand.app_name} className="h-10 w-10 object-contain" />
              ) : (
                <img src="/smarto-icon.png" alt="SmartoERP" className="h-10 w-10 object-contain" style={{ background: 'transparent' }} />
              )}
            </div>
            <span className="text-2xl font-bold font-outfit tracking-wider text-white drop-shadow-lg">{(brand.app_name || 'SMARTO').toUpperCase()}</span>
          </div>

          {/* Brand Name - Premium Typography */}
          <h1 className="font-outfit font-black tracking-[0.35em] text-white drop-shadow-lg" style={{ fontSize: '4.5rem', lineHeight: 1, textShadow: '0 4px 30px rgba(0,0,0,0.15)' }}>
            {(brand.app_name || 'SMARTO').toUpperCase()}
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center gap-3 mt-4 mb-3">
            <div className="w-12 h-[1px] bg-white/40" />
            <div className="w-2 h-2 rounded-full bg-white/60" />
            <div className="w-12 h-[1px] bg-white/40" />
          </div>

          {/* iter692 — "by Smart Standards" tagline removed per operator request. */}

          {/* Tagline */}
          <p className="text-white/50 text-xs mt-2 tracking-widest uppercase">{brand.tagline || 'Business Management Platform'}</p>

          {/* Feature pills */}
          <div className="mt-14 flex flex-wrap justify-center gap-3">
            {['Sales & Invoices', 'HR & Payroll', 'Accounting', 'Reports & Analytics'].map((label) => (
              <div key={label} className="px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-sm text-white/80 font-medium tracking-wide">
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/smarto-logo-main.png" alt="Smarto" className="h-16 object-contain" />
          </div>
          <Card className="border-border shadow-xl shadow-orange-500/5" data-testid="login-card">
            <CardHeader className="space-y-2 pb-4">
              <CardTitle className="text-2xl font-bold font-outfit">Welcome back</CardTitle>
              <CardDescription>Sign in to your Smarto account</CardDescription>
            </CardHeader>
            <CardContent>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-button"
              className="w-full h-11 rounded-xl shadow-lg transition-all duration-300"
              style={{ background: `linear-gradient(to right, ${brand.primary_color || '#ea580c'}, ${brand.accent_color || '#f97316'})` }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-muted" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-muted" />
          </div>

          {/* Google Sign-In */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            data-testid="google-login-button"
            className="w-full h-11 rounded-xl border-border hover:bg-muted/50 transition-all"
            onClick={() => {
              const redirectUrl = window.location.origin;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <div className="mt-5 text-center text-sm space-y-2">
            <div>
              <Link to="/forgot-password" className="text-orange-600 hover:underline font-medium" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <div>
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-orange-600 hover:underline font-medium" data-testid="register-link">
                Sign up
              </Link>
            </div>
            <div className="pt-3 flex items-center justify-center gap-2 flex-wrap" data-testid="login-footer-stamps">
              <BuildStamp testid="login-build-stamp" />
              <NewBadge since="2026-04-27" featureId="login-build-stamp-shipped" size="xs" />
              <ApkStamp />
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
